import { useCallback, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import {
  Dependency,
  DependencyLinkUpdates,
  useDependencyContext,
} from './DependencyContext'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
} from '../utils/linkInfoPopupUtils'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'

export interface DepPopupState {
  dependency: Dependency
  fromName: string
  toName: string
  fromIconUrl?: string
  toIconUrl?: string
}

export const useDependencyLinePopup = (
  api: GridApi | undefined,
  dataPath?: string
) => {
  const dependencyContext = useDependencyContext()
  const [depPopup, setDepPopup] = useState<DepPopupState | null>(null)

  const handleLineClick = useCallback(
    (dep: Dependency, event: React.MouseEvent) => {
      const prefix = dataPath ? `${dataPath}.` : ''
      const fromName = api
        ? getWbsItemDisplayName(api, dep.fromRowIndex, prefix)
        : ''
      const toName = api
        ? getWbsItemDisplayName(api, dep.toRowIndex, prefix)
        : ''
      const fromIconUrl = api
        ? getWbsItemIconUrl(api, dep.fromRowIndex, prefix)
        : undefined
      const toIconUrl = api
        ? getWbsItemIconUrl(api, dep.toRowIndex, prefix)
        : undefined
      setDepPopup({
        dependency: dep,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
      })
    },
    [api, dataPath]
  )

  const handleDelete = useCallback(() => {
    if (depPopup && dependencyContext) {
      dependencyContext.removeDependency(
        depPopup.dependency.fromRowIndex,
        depPopup.dependency.toRowIndex
      )
    }
    setDepPopup(null)
  }, [depPopup, dependencyContext])

  const handleUpdate = useCallback(
    (result: LinkEditorResult) => {
      if (!depPopup || !dependencyContext) return
      const updates: DependencyLinkUpdates = {}
      if (result.color !== undefined) updates.color = result.color
      if (result.description !== undefined) {
        updates.description = result.description
      }
      if (Object.keys(updates).length > 0) {
        dependencyContext.updateDependencyLink(
          depPopup.dependency.uuid,
          updates
        )
      }
      setDepPopup(null)
    },
    [depPopup, dependencyContext]
  )

  const closePopup = useCallback(() => setDepPopup(null), [])

  // Open popup with pre-resolved info (for fallback links where one endpoint is hidden)
  const openPopup = useCallback(
    (state: DepPopupState) => setDepPopup(state),
    []
  )

  return {
    depPopup,
    handleLineClick,
    handleDelete,
    handleUpdate,
    closePopup,
    openPopup,
  }
}
